import React, { useContext, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import Config from "../utils/Config";
import "./Login.css";

const PendingFFLClaims = () => {
  const { claimid } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [pendingClaim, setPendingClaim] = useState(null); // ✅ Store as a single object
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);

  useEffect(() => {
    if (!user || !claimid) return;

    const fetchPendingClaim = async () => {
      try {
        const response = await fetch(
          `${Config.API.API_BASE_URL}${Config.ENDPOINTS.DEALERS_GET_PENDING_FFL_CLAIM}?claimid=${claimid}`,
          {
            method: "GET",
            headers: {
              "Authorization": `Bearer ${localStorage.getItem("authToken")}`,
            },
          }
        );

        const data = await response.json();
        if (response.ok) {
          setPendingClaim(data); // ✅ Store as a single object
        } else {
          setError(data.message || "Failed to fetch claim.");
        }
      } catch (err) {
        setError("Error fetching FFL claim.");
      } finally {
        setLoading(false);
      }
    };

    fetchPendingClaim();
  }, [user, claimid,navigate]); // ✅ Trigger API when `user` or `claimid` changes

  // ✅ Open the delete modal
  const openDeleteModal = () => {
    setSelectedClaim(pendingClaim);
    setShowDeleteModal(true);
  };

  // ✅ Close the delete modal
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedClaim(null);
  };

  // ✅ Open the delete modal
  const openApproveModal = () => {
    setSelectedClaim(pendingClaim);
    setShowApproveModal(true);
  };

  // ✅ Close the delete modal
  const closeApproveModal = () => {
    setShowApproveModal(false);
    setSelectedClaim(null);
  };

  // ✅ Function to delete a claim
  const handleDeleteClaim = async () => {
    if (!selectedClaim) return;

    try {
      const response = await fetch(
        `${Config.API.API_BASE_URL}${Config.ENDPOINTS.DEALERS_PENDING_FFL_CLAIMS}${selectedClaim._id}`,
        {
          method: "DELETE",
          headers: {
            "Authorization": `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        setPendingClaim(null); // ✅ Remove claim from UI after deletion
      }
    } catch (err) {
      console.error("Error deleting claim:", err);
    }

    closeDeleteModal();
  };

  // ✅ Function to approve a claim
  const handleApproveClaim = async () => {
    if (!selectedClaim) return;

    try {
      const response = await fetch(
        `${Config.API.API_BASE_URL}${Config.ENDPOINTS.DEALERS_APPROVE_FFL_CLAIM}/${selectedClaim._id}`,
        {
          method: "PUT",
          headers: {
            "Authorization": `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        }
      );
  
      if (response.ok) {
        setPendingClaim(null); // ✅ Remove claim from UI after approval
      } else {
        const data = await response.json();
        console.error("Error approving claim:", data.message);
      }
    } catch (err) {
      console.error("Error approving claim:", err);
    }

    closeApproveModal();
  };

  return (
    <div className="login-container" id="claim-container">
      <div className="login-box" id="admin-pending-ffl-claim">
        <h2>Pending FFL Claim</h2>

        {loading && <p>Loading...</p>}
        {error && <p className="error">{error}</p>}

        {!loading && !error && !pendingClaim && <p>No pending FFL claim found.</p>}

        {!loading && !error && pendingClaim && (
          <div className="pending-claim-details">
            <strong>FFL Number:</strong> <a target='_blank' rel="noopener noreferrer" href={`${Config.API.SITE_BASE_URL}/dealer/${pendingClaim.dealerId}`}>{pendingClaim.fflNumber}</a> <br />
            <strong>FFL Name:</strong> {pendingClaim.fflName} <br />
            <strong>Created:</strong> <span className="claim-date">{new Date(pendingClaim.createdAt).toLocaleDateString()}</span> <br />
            <strong>Created By:</strong> {pendingClaim.userEmail}
            <br />
            <button className="approve-claim-btn" onClick={openApproveModal}>✅ Approve Claim</button>
            <button className="delete-claim-btn" onClick={openDeleteModal}>❌ Cancel Claim</button>
          </div>
        )}
      </div>

      {/* ✅ Custom Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Cancel FFL Claim</h3>
            <p>Are you sure you want to cancel this claim?</p>
            <strong>FFL: {selectedClaim?.fflNumber}</strong>
            <br />
            <strong>Name: {selectedClaim?.fflName}</strong>
            <div className="modal-buttons">
              <button className="confirm-delete" onClick={handleDeleteClaim}>Yes, Cancel</button>
              <button className="cancel-delete" onClick={closeDeleteModal}>No, Keep it</button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Custom Approve Confirmation Modal */}
      {showApproveModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Approve FFL Claim</h3>
            <p>Are you sure you want to approve this claim?</p>
            <strong>FFL: {selectedClaim?.fflNumber}</strong>
            <br />
            <strong>Name: {selectedClaim?.fflName}</strong>
            <div className="modal-buttons">
              <button className="cancel-delete" onClick={handleApproveClaim}>Yes, Approve</button>
              <button className="confirm-delete" onClick={closeApproveModal}>No, Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingFFLClaims;
